import { useState, type ReactNode } from 'react'

type Props = {
  icon: string
  deviceLabel?: string
  title: string
  subtitle: string
  helper: string
  submitLabel: string
  submittingLabel: string
  onSubmit: (pin: string) => Promise<void>
  footer?: ReactNode
}

const MAX_DIGITS = 6
const KEYPAD_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

/**
 * Kartu login PIN full-page (bukan modal) — dipakai di halaman /warehouse/login
 * dan /delman/login. Mirror pola PinLoginPage / PinKeypadModal superoti-kitchen,
 * dikonsolidasi jadi satu komponen agar dua mode build tidak duplikasi keypad UI.
 */
export function PinEntryCard({
  icon,
  deviceLabel,
  title,
  subtitle,
  helper,
  submitLabel,
  submittingLabel,
  onSubmit,
  footer,
}: Props) {
  const [pin, setPin] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shakeKey, setShakeKey] = useState(0)

  const canSubmit = pin.length === MAX_DIGITS && !isSubmitting

  function addDigit(digit: string) {
    if (isSubmitting) return
    setPin((prev) => (prev.length >= MAX_DIGITS ? prev : `${prev}${digit}`))
    setError(null)
  }

  function removeDigit() {
    if (isSubmitting) return
    setPin((prev) => prev.slice(0, -1))
    setError(null)
  }

  async function handleSubmit() {
    if (!canSubmit) return
    setIsSubmitting(true)
    setError(null)
    try {
      await onSubmit(pin)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login gagal. Coba lagi.')
      setPin('')
      setShakeKey((prev) => prev + 1)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="w-full max-w-[480px]">
      <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="bg-surface pt-8 pb-6 px-gutter-tablet sm:px-gutter-desktop flex flex-col items-center border-b border-surface-variant">
          <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mb-4 text-on-primary-container shadow-sm border border-outline-variant/30">
            <span className="material-symbols-outlined text-[32px] icon-fill">{icon}</span>
          </div>
          {deviceLabel && <p className="text-label-md text-on-surface-variant mb-2 text-center">{deviceLabel}</p>}
          <h1 className="text-headline-md text-primary mb-1 text-center">{title}</h1>
          <p className="text-body-sm text-on-surface-variant text-center">{subtitle}</p>
        </div>

        <div className="p-gutter-tablet sm:p-gutter-desktop flex flex-col gap-5">
          <div key={shakeKey} className="flex justify-center gap-2 pin-shake" aria-label="PIN indicator">
            {Array.from({ length: MAX_DIGITS }).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full border border-outline ${
                  i < pin.length ? 'bg-primary border-primary' : 'bg-surface'
                }`}
              />
            ))}
          </div>

          <div className={`grid grid-cols-3 gap-3 transition-opacity ${isSubmitting ? 'opacity-60' : 'opacity-100'}`}>
            {KEYPAD_DIGITS.map((digit) => (
              <button
                key={digit}
                type="button"
                disabled={isSubmitting}
                onClick={() => addDigit(digit)}
                className="pin-key"
                aria-label={`Angka ${digit}`}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={removeDigit}
              className="pin-key"
              aria-label="Hapus digit terakhir"
            >
              <span className="material-symbols-outlined text-xl">backspace</span>
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => addDigit('0')}
              className="pin-key"
              aria-label="Angka 0"
            >
              0
            </button>
            <div />
          </div>

          {error && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{error}</span>
            </div>
          )}
          {!error && pin.length < MAX_DIGITS && (
            <p className="text-body-sm text-on-surface-variant text-center">{helper}</p>
          )}

          <button type="button" disabled={!canSubmit} onClick={handleSubmit} className="btn-cta btn-tactile">
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>

        {footer}
      </div>
    </main>
  )
}
